<script>
    import {sgettext} from '../../i18n.js';
    import {submit} from '../../utils.js';
    import {PATH_PREFIX} from "../../apiUrls.js"
    import {onMount} from 'svelte';
    import AttachmentFile from './AttachmentFile.svelte';

    export let id = 'mediaInput';
    export let activityVersionId;
    export let key = '';
    export let media = null;
    export let onChange = () => {};
    export let onCancel = () => {};
    export let showLibrary = null;
    export let maxFiles = 5;
    export let cleanupAttachments = false;
    export let attachments = [];
    export let readOnly = false;
    export let allowFileModifications = false; // Allow modifications to active attachments

    let url = `${PATH_PREFIX}/sw/v2/api/activity_version/${activityVersionId}/attachment/`
    let urlReadOnly = `${PATH_PREFIX}/sw/v2/api/activity_version/${activityVersionId}/attachment-readonly/`


    let dragOver = false;
    let fileInput = null;

    function handleUpload(files) {
        const fileList = Array.from(files);
        fileList.forEach((media) => {
            let isDuplicate = attachments.some(attachment => attachment.name === media.name);
            if (isDuplicate || (attachments?.length === maxFiles)) {
                console.error(`File upload not allowed: "${media.name}" - max files (${maxFiles}) reached or duplicate file.`);
                return;
            }
            attachments=[...attachments, media];
            handleMediaInputChanged(media);
        });
    }

    function onFileInputChange(event) {
        handleUpload(event.currentTarget.files)
    }

    function handleMediaInputChanged(media) {
        onChange(media);
    }

    function handleDragEnter(event) {
        dragOver = true;
    }

    function handleDragLeave(event) {
        dragOver = false;
    }

    function handleDrop(event) {
        dragOver = false;
        handleUpload(event.dataTransfer.files)
    }

    export function showFileBrowser(){
        fileInput.click();
    }

    function handleBrowseMedia(){
        if(!showLibrary){
            fileInput.click();
        } else {
            showLibrary();
        }
    }

    function handleDragOver(event) {
        // required so that browser will not open the media
        dragOver = true;
    }

    async function removeFile(event) {
        let id = event.detail.id;
        if (id) {cleanupPreviewFiles([id])}
        attachments = attachments.filter(attachment => attachment.name !== event.detail.name);
    }

    async function cleanupPreviewFiles(id) {
        await submit(`${url}remove_attachment/`, 'POST', {id:id});
    }

    async function retrieveAttachments() {
        const response = await fetch(urlReadOnly);
        if (response.ok) {
            const json = await response.json();
            if (json) {
                attachments=[]
                attachments=json.results.map(item => ({...item,name: item.file_name}));
            }
        }
    }

    $:  {
        if(cleanupAttachments){
            let ids = attachments.filter(attachment => attachment.id !== undefined).map(attachment => attachment.id); 
            if (ids?.length) {
                cleanupPreviewFiles(ids);
            }
            cleanupAttachments = false;
            retrieveAttachments();
        }
    }

    onMount(() => {
        retrieveAttachments();
    });

</script>

<div class="media-uploader" class:drag-over={dragOver}>
  {#key cleanupAttachments}
    <input
      {id}
      {key}
      bind:this={fileInput}
      class="hidden"
      type="file"
      multiple
      on:change={onFileInputChange}
    />
    <div
      class="drop-area"
      on:dragenter|preventDefault|stopPropagation={handleDragEnter}
      on:dragleave|preventDefault|stopPropagation={handleDragLeave}
      on:dragover|preventDefault|stopPropagation={handleDragOver}
      on:drop|preventDefault|stopPropagation={handleDrop}
      on:dragend
      on:dragstart|preventDefault={() => {}}
    >
      <div class="placeholder" on:click={handleBrowseMedia}>
        {#if attachments?.length !== maxFiles}
          <label class="drop-label">
            {sgettext('$$DRAG_DROP_FILES$$')}
            <span class="filepond--label-action hover-bold"
              ><u>{sgettext('$$BROWSE_1$$')}</u></span
            >
          </label>
        {/if}
      </div>
      <div
        style="padding-top: {attachments?.length === maxFiles
          ? '15px'
          : '80px'};"
      >
        <table class="table default-table-overrides">
          {#each attachments as file}
            <AttachmentFile
              {url}
              {readOnly}
              media={file}
              key={file.name}
              {activityVersionId}
              {allowFileModifications}
              {cleanupAttachments}
              on:removeFile={removeFile}
            />
          {/each}
        </table>
      </div>
    </div>
  {/key}
</div>

<style>
  .hidden {
    display: none;
  }

  .media-uploader {
    background-color: #edf0f4;
    border-radius: 10px;
    width: 100%;
    min-height: 100px;
    font-size: 1rem;
    line-height: normal;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
      Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji',
      'Segoe UI Symbol';
    font-weight: 450;
    text-align: left;
    text-rendering: optimizeLegibility;
    direction: ltr;
  }

  .default-table-overrides {
    background-color: #edf0f4;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }

  .drop-area {
    text-align: center;
    position: relative;
    cursor: pointer;
  }

  .drag-over {
    box-shadow: 0 0 5px #dd5900;
  }

  .placeholder {
    padding-top: 10px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    position: absolute;
    color: #4f4f4f;
  }

  .drop-label {
    font-weight: normal;
    padding-top: 20px;
    cursor: pointer;
  }

  .hover-bold:hover {
    font-weight: bold;
  }
</style>
